import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import usePermissions from "amnShared/Permissions/usePermissions";
import useApproval from "amnPos/Approvals/useApproval";
import { PermissionType } from "amnShared/Permissions/IPermissions";
import { GetNeededApproval } from "amnPos/Approvals/approvalHelper";
import { IAllowedApproval } from "amnPos/ticket/Ticket";
import { useAppSelector } from "app/hooks";
import { UserState } from "adminPanel/user/userSlice";

interface ApprovalGuardProps {
    children: ReactNode;
    permissionType?: PermissionType;
    redirectPath: string;
}

export default function ApprovalGuard({
    children,
    permissionType,
    redirectPath,
}: ApprovalGuardProps) {
    const { canEmployee } = usePermissions();
    const { AskSupervisorApproval } = useApproval();
    const user = useAppSelector((state) => state.user as UserState);

    const location = useLocation();
    const { id } = useParams<{ id?: string }>();
    const isPreApproved = location.state?.approved;

    // State to track approval status: 'checking', 'approved', 'denied'
    // When permissionType is undefined, we default to 'approved' (true)
    const [isApproved, setIsApproved] = useState<boolean>(!permissionType || isPreApproved);
    const [isChecking, setIsChecking] = useState<boolean>(!!permissionType && !isPreApproved);

    // Clear the approved state from history so a reload will require checking again
    const navigate = useNavigate();
    useEffect(() => {
        if (isPreApproved) {
            navigate(location.pathname, { replace: true, state: { ...location.state, approved: undefined } });
        }
    }, [isPreApproved, location.pathname, location.state, navigate]);

    useEffect(() => {
        // If no permission required, we are good.
        if (!permissionType || isPreApproved) return;

        // Check permission
        // We pass PermissionType.NONE if undefined but we guarded above.
        const perm = canEmployee(permissionType);

        if (perm.response) {
            setIsApproved(true);
            setIsChecking(false);
            return;
        }

        // If we're already checking or already denied/approved, don't re-trigger
        // (This works because this component mounts ONCE for the route)

        const neededApproval = GetNeededApproval(perm.error);

        AskSupervisorApproval(
            neededApproval,
            (approval: IAllowedApproval[]) => {
                // success
                setIsApproved(true);
                setIsChecking(false);
            },
            () => {
                // failure
                setIsApproved(false);
                setIsChecking(false);
            },
            permissionType === PermissionType.CanViewCustomerBalanceStatement && id
                ? { clientId: id }
                : {} // Additional info
        );

    }, [permissionType]); // Only re-run if permission requirement changes


    if (isChecking) {
        // While waiting for supervisor approval dialog...
        // We render nothing (or a spinner/overlay if desired, but dialog is already an overlay)
        // IMPORTANT: The Dialog logic is in Context, so it renders 'outside' this component tree slightly.
        // Rendering null keeps the current background (if in a nested route) or blank screen.
        return null;
    }

    if (isApproved) {
        return <>{children}</>;
    }

    // If denied (and finished checking), redirect
    return <Navigate to={redirectPath} replace />;
}
